import { computed, watch } from 'vue'
import { LANDING_CATEGORY_IDS } from '@/constants/landing-categories'
import { useIndexRoutes } from '@/composables/locale/useIndexRoutes'
import { useRouteLocale } from '@/composables/locale'
import { useLandingCategoriesStore } from '@/stores/landing-categories'
import { capitalizeFirst } from '@/utils/helpers'

export interface LandingCategoryForUI {
  id: string
  title: string
  image: string
  imageMobile: string
  imageAlt: string
  imageMobileAlt: string
  href: string
  bgColor: string
}

export function useCourseCategory() {
  const { t } = useI18n()
  const { buildPath } = useIndexRoutes()
  const { currentLocale } = useRouteLocale()
  const landingCategoriesStore = useLandingCategoriesStore()

  function ensureCategoriesForLocale(locale: string) {
    if (!locale || landingCategoriesStore.isLoadedForLocale(locale)) return
    landingCategoriesStore.fetchCategoriesForLocale(locale).catch(() => {})
  }

  watch(currentLocale, (locale) => ensureCategoriesForLocale(locale), { immediate: true })

  const landingCategories = computed<LandingCategoryForUI[]>(() => {
    const allowedIds = landingCategoriesStore.getCategoryIdsForLocale(currentLocale.value)
    return LANDING_CATEGORY_IDS
      .filter((c) => allowedIds.includes(c.id))
      .map((c) => ({
        id: c.id,
        title: capitalizeFirst(t(c.labelKey)),
        image: c.image,
        imageMobile: c.imageMobile,
        imageAlt: c.imageAlt,
        imageMobileAlt: c.imageMobileAlt,
        href: buildPath({ category: c.id }),
        bgColor: c.bgColor,
      }))
  })

  return {
    landingCategories,
    loadingCategories: computed(() => landingCategoriesStore.loading),
    categoriesError: computed(() => landingCategoriesStore.error),
  }
}
